import type { AxiosInstance } from "axios";
import { AlertTriangle, ArrowLeft, CheckCircle2, ChevronRight, RefreshCw, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { InfoTooltip } from "../components/ui/info-tooltip.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { apiErrorMessage } from "../lib/api.js";
import { fetchHealth, timeAgo, type HealthBoard, type HealthPipeline, type HealthStatus, type StoreHealthRow } from "../lib/health.js";

/**
 * Store Health — "is my tracking working?", turned from a support ticket into a page.
 *
 * THE REASON THIS SCREEN EXISTS is incident I5: a store's tracking was off for a MONTH
 * while orders kept arriving, and nobody noticed because every dashboard the merchant
 * looked at was happy to show smaller numbers without saying why. Every rule behind these
 * verdicts names the incident it came from, and this screen prints that reason — a red
 * badge with no explanation is just an alarm, and people learn to ignore alarms.
 *
 * IT SHOWS ALL OF THE MERCHANT'S STORES AT ONCE, not the one in the store picker. "Which
 * of my stores is broken" is the question; answering it one store at a time would make
 * someone click through every store to find the dead one.
 */
export function HealthScreen({
  client,
  tenantId,
}: {
  readonly client: AxiosInstance;
  /** Not sent — the board is scoped by session. Present so switching stores refetches. */
  readonly tenantId: string;
}): React.JSX.Element {
  const [board, setBoard] = useState<HealthBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBoard(await fetchHealth(client));
    } catch (caught) {
      /**
       * The board is CLEARED on failure, deliberately. A stale board is the one thing
       * this screen must never show: "everything green, as of some earlier moment" is
       * exactly the false reassurance I5 was made of.
       */
      setBoard(null);
      setError(apiErrorMessage(caught, "Could not load store health."));
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  const selected = selectedId === null ? null : (board?.stores.find((row) => row.store.id === selectedId) ?? null);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Store Health</h1>
          <p className="text-muted-foreground text-sm">
            {selected === null
              ? "Whether your tracking, orders and ad data are arriving right now."
              : selected.store.shopDomain}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {board !== null && <span className="text-muted-foreground text-xs">Checked {timeAgo(board.generatedAt)}</span>}
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={loading ? "animate-spin" : undefined} />
            Refresh
          </Button>
        </div>
      </div>

      {error !== null && (
        <Card className="border-destructive/40">
          <CardContent className="text-destructive pt-0 text-sm">{error}</CardContent>
        </Card>
      )}

      {loading && board === null && (
        <div className="grid gap-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {!loading && error === null && board?.stores.length === 0 && (
        <Card>
          <CardContent className="text-muted-foreground pt-0 text-sm">
            No stores to report on yet. Once a store is connected its health appears here.
          </CardContent>
        </Card>
      )}

      {/* DETAIL — one store, opened from the list. */}
      {selected !== null && (
        <>
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-sm"
          >
            <ArrowLeft className="size-4" /> All stores
          </button>
          <StoreCard row={selected} />
        </>
      )}

      {/* LIST — the platform freshness, the pipelines, then a row per store. */}
      {selected === null && board !== null && board.stores.length > 0 && (
        <>
          <FreshnessNotice freshness={board.dataFreshness} />
          {board.pipelines.length > 0 && <PipelinesCard pipelines={board.pipelines} />}
          <div className="bg-card overflow-hidden rounded-lg border">
            {board.stores.map((row, index) => (
              <StoreListRow key={row.store.id} row={row} first={index === 0} onOpen={() => setSelectedId(row.store.id)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * One store in the list: its light, its name, and how many issues — enough to decide
 * WHICH store to open. The detail (signals, per-tier ad sync, the findings that explain
 * each warning) is one click away, not stacked on screen for every store at once.
 */
function StoreListRow({
  row,
  first,
  onOpen,
}: {
  readonly row: StoreHealthRow;
  readonly first: boolean;
  readonly onOpen: () => void;
}): React.JSX.Element {
  const issues = row.verdict.findings.length;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`hover:bg-accent flex w-full items-center gap-3 px-4 py-3 text-left ${first ? "" : "border-t"}`}
    >
      <StatusIcon status={row.verdict.status} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{row.store.displayName}</div>
        <div className="text-muted-foreground truncate text-xs">{row.store.shopDomain}</div>
      </div>
      <span className="text-muted-foreground shrink-0 text-xs">
        {row.verdict.status === "green" ? "Healthy" : `${String(issues)} issue${issues === 1 ? "" : "s"}`}
      </span>
      <ChevronRight className="text-muted-foreground size-4 shrink-0" />
    </button>
  );
}

/**
 * Whether the PLATFORM is behind, shown separately from any one store.
 *
 * Without this, every store can read green while attribution has not run for two hours,
 * and the board is lying by omission — the numbers on every other page would be stale
 * with nothing on screen to say so.
 */
function FreshnessNotice({
  freshness,
}: {
  readonly freshness: HealthBoard["dataFreshness"];
}): React.JSX.Element | null {
  if (freshness.status === "green") return null;

  return (
    <Card className="border-amber-300 dark:border-amber-900">
      <CardContent className="flex items-center gap-2 pt-0 text-sm text-black dark:text-black">
        <AlertTriangle className="size-4 shrink-0" />
        <span>
          Your numbers may be behind
          {freshness.minutesBehind === null
            ? "."
            : ` by up to ${String(freshness.minutesBehind)} minutes — our processing is catching up.`}
        </span>
      </CardContent>
    </Card>
  );
}

/**
 * Plain-language explanations, one per pipeline the board can show — keyed by the same
 * human `label` `apps/api`'s `pipelineLabel` produces (.ai — apps/api/src/modules/health/
 * health.service.ts), so a merchant hovering never sees "ch-writer:events.v2" or
 * "jobs-attribution", only what that number means for their store. Written for the
 * merchant reading this screen, not for whoever built the pipeline — no queue names, no
 * table names, no internal jargon.
 */
const PIPELINE_TOOLTIPS: Readonly<Record<string, string>> = {
  "Cart tracking": "How current your cart data is — this feeds abandoned-cart tracking and cart reports.",
  "Event tracking": "How current your visitor tracking is — page views, clicks and sessions from your storefront.",
  "Order tracking": "How current your order data is — new orders and their details as they arrive from Shopify.",
  "Refund tracking": "How current your refund data is — refunds are naturally less frequent than orders, so this can go a few days between updates without anything being wrong.",
  "Data deletion requests": "How current we are on customer data-deletion requests — these are rare, so this only appears when one has come in recently.",
  "Attribution engine": "How current your marketing attribution is — deciding which ad or channel gets credit for each sale.",
  "Ad sync": "How recently we pulled in your latest ad spend and performance from Meta and Google.",
};

/**
 * The engines behind the numbers, and when each last ran.
 *
 * The board used to hide these behind one "data freshness" line; the reason I5 stays
 * unlearned if "attribution has not run in two hours" is not something a person can see.
 */
function PipelinesCard({ pipelines }: { readonly pipelines: readonly HealthPipeline[] }): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Processing pipelines</CardTitle>
        <CardDescription>When each engine behind your numbers last completed.</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-2 sm:grid-cols-2">
          {pipelines.map((pipeline) => {
            const tooltip = PIPELINE_TOOLTIPS[pipeline.label];
            return (
              <div key={pipeline.key} className="rounded-md border p-3 text-sm">
                <div className="flex items-center gap-2">
                  <StatusIcon status={pipeline.status} />
                  <span className="font-medium">{pipeline.label}</span>
                  {tooltip !== undefined && <InfoTooltip text={tooltip} />}
                  <span className="text-muted-foreground ml-auto text-xs">last ran {timeAgo(pipeline.lastSuccessAt)}</span>
                </div>
                {pipeline.reason !== null && (
                  <p className="text-muted-foreground mt-1.5 pl-6 text-xs">{pipeline.reason}</p>
                )}
              </div>
            );
          })}
        </dl>
      </CardContent>
    </Card>
  );
}

function StoreCard({ row }: { readonly row: StoreHealthRow }): React.JSX.Element {
  const { store, signals, verdict, adSyncs } = row;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            <StatusIcon status={verdict.status} />
            <span className="truncate">{store.displayName}</span>
          </CardTitle>
          <p className="text-muted-foreground truncate text-xs">{store.shopDomain}</p>
        </div>
        <StatusBadge status={verdict.status} />
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Signal label="Last event" value={timeAgo(signals.lastEventAt)} />
          <Signal label="Last order" value={timeAgo(signals.lastOrderAt)} />
          <Signal label="Sessions (48h)" value={signals.sessions48h.toLocaleString("en-US")} />
          <Signal label="Orders (48h)" value={signals.orders48h.toLocaleString("en-US")} />
        </dl>

        {adSyncs.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-muted-foreground text-xs">Ad sync</span>
            {adSyncs.map((sync) => (
              <div key={sync.platform} className="rounded-md border p-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <StatusIcon status={sync.status} />
                  <span className="font-medium capitalize">{sync.platform}</span>
                  {sync.circuitOpen && (
                    <Badge variant="outline" className="border-red-300 text-red-700 dark:text-red-400">
                      paused after {sync.consecutiveFailures} failures
                    </Badge>
                  )}
                  <span className="text-muted-foreground ml-auto text-xs">last synced {timeAgo(sync.lastSuccessAt)}</span>
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Signal label="Hot (5h)" value={timeAgo(sync.lastHotAt)} />
                  <Signal label="Warm (daily)" value={timeAgo(sync.lastWarmAt)} />
                  <Signal label="Cold (backfill)" value={timeAgo(sync.lastColdAt)} />
                  <Signal label="Manual" value={timeAgo(sync.lastManualAt)} />
                </dl>
              </div>
            ))}
          </div>
        )}

        {verdict.findings.length > 0 && (
          <ul className="flex flex-col gap-2">
            {verdict.findings.map((finding) => (
              <li
                key={finding.code}
                className="bg-muted/50 flex items-start gap-2 rounded-md border p-3 text-sm"
              >
                <StatusIcon status={finding.severity} className="mt-0.5" />
                <div className="min-w-0">
                  <p>{finding.message}</p>
                  {/*
                    The REASON, in the merchant's view rather than only in ops'. A red
                    badge with no explanation is an alarm, and people learn to ignore
                    alarms; a rule that can say which incident it came from is one a
                    merchant can act on.
                  */}
                  <p className="text-muted-foreground mt-0.5 text-xs">{finding.because}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Signal({ label, value }: { readonly label: string; readonly value: string }): React.JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="truncate text-sm font-medium">{value}</dd>
    </div>
  );
}

function StatusIcon({
  status,
  className,
}: {
  readonly status: HealthStatus;
  readonly className?: string;
}): React.JSX.Element {
  if (status === "red") return <XCircle className={`size-4 shrink-0 text-[var(--health-red)] ${className ?? ""}`} />;
  if (status === "amber")
    return <AlertTriangle className={`size-4 shrink-0 text-[var(--health-amber)] ${className ?? ""}`} />;
  return <CheckCircle2 className={`size-4 shrink-0 text-[var(--health-green)] ${className ?? ""}`} />;
}

function StatusBadge({ status }: { readonly status: HealthStatus }): React.JSX.Element {
  const label = status === "green" ? "Healthy" : status === "amber" ? "Needs attention" : "Not working";
  return (
    <Badge
      variant="outline"
      className="shrink-0 gap-1.5"
      style={{ borderColor: `var(--health-${status})`, color: `var(--health-${status})` }}
    >
      {label}
    </Badge>
  );
}
